import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, Circle, Clock, AlertTriangle, Plus, 
  Image, Package, Settings, Users, MoreVertical,
  Calendar, Target, TrendingUp, ChevronRight, Loader2,
  Trash2, Edit, MessageSquare
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTasksPage = () => {
  const { country } = useParams();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [imageStats, setImageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewTask, setShowNewTask] = useState(false);
  const [filter, setFilter] = useState('all');

  // New task form
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'medium',
    target_count: '',
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, statsRes, imageStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/tasks`),
        fetch(`${API_URL}/api/admin/tasks/stats/summary`),
        fetch(`${API_URL}/api/admin/images/stats`)
      ]);
      
      const tasksData = await tasksRes.json();
      const statsData = await statsRes.json();
      const imageStatsData = await imageStatsRes.json();
      
      setTasks(tasksData);
      setStats(statsData);
      setImageStats(imageStatsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...newTask,
        target_count: newTask.target_count ? parseInt(newTask.target_count) : null
      };
      
      await fetch(`${API_URL}/api/admin/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData)
      });
      
      setShowNewTask(false);
      setNewTask({ title: '', description: '', category: 'other', priority: 'medium', target_count: '', due_date: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/admin/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const updateTaskProgress = async (taskId, completedCount) => {
    try {
      await fetch(`${API_URL}/api/admin/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed_count: completedCount })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/tasks/${taskId}`, {
        method: 'DELETE'
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    return task.status === filter;
  });

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'images': return <Image className="w-4 h-4" />;
      case 'products': return <Package className="w-4 h-4" />;
      case 'partner': return <Users className="w-4 h-4" />;
      case 'system': return <Settings className="w-4 h-4" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-blue-500" />;
      case 'pending': return <Circle className="w-5 h-5 text-gray-400" />;
      default: return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aug-cream flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-aug-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Painel de Tarefas
            </h1>
            <p className="text-gray-600">
              Gerencie tarefas, acompanhe o progresso de uploads e organize o trabalho diário.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Image Upload Progress */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Image className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {imageStats?.percent_complete || 0}%
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Imagens de Produtos</h3>
              <p className="text-sm text-gray-500">
                {imageStats?.with_images || 0} de {imageStats?.total_products || 0} produtos
              </p>
              <div className="mt-3 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${imageStats?.percent_complete || 0}%` }}
                />
              </div>
            </div>

            {/* Today's Progress */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <span className="text-2xl font-bold text-green-600">
                  {imageStats?.uploads_today || 0}/{imageStats?.daily_target || 50}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Meta de Hoje</h3>
              <p className="text-sm text-gray-500">
                {imageStats?.daily_progress_percent || 0}% da meta diária
              </p>
              <div className="mt-3 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(imageStats?.daily_progress_percent || 0, 100)}%` }}
                />
              </div>
            </div>

            {/* Pending Tasks */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <span className="text-2xl font-bold text-orange-600">
                  {stats?.pending || 0}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Tarefas Pendentes</h3>
              <p className="text-sm text-gray-500">
                {stats?.in_progress || 0} em andamento
              </p>
            </div>

            {/* Completed Tasks */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-purple-600" />
                </div>
                <span className="text-2xl font-bold text-purple-600">
                  {stats?.completed || 0}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Concluídas</h3>
              <p className="text-sm text-gray-500">
                Total: {stats?.total || 0} tarefas
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowNewTask(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nova Tarefa
              </button>
              <button
                onClick={() => navigate(`/${country}/admin/images`)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Image className="w-4 h-4" />
                Upload de Imagens
              </button>
              <button
                onClick={() => window.open('/memory/ROADMAP.md', '_blank')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Ver Roadmap
              </button>
            </div>
          </div>

          {/* Task Filters */}
          <div className="flex gap-2 mb-6">
            {['all', 'pending', 'in_progress', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {status === 'all' && 'Todas'}
                {status === 'pending' && 'Pendentes'}
                {status === 'in_progress' && 'Em Andamento'}
                {status === 'completed' && 'Concluídas'}
              </button>
            ))}
          </div>

          {/* Tasks List */}
          <div className="space-y-4">
            {filteredTasks.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
                <Circle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma tarefa encontrada</h3>
                <p className="text-gray-500 mb-4">Crie sua primeira tarefa para começar a organizar o trabalho.</p>
                <button
                  onClick={() => setShowNewTask(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Criar Tarefa
                </button>
              </div>
            ) : (
              filteredTasks.map((task) => (
                <div
                  key={task.id}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {/* Status Icon */}
                    <button
                      onClick={() => {
                        const nextStatus = task.status === 'pending' ? 'in_progress' 
                          : task.status === 'in_progress' ? 'completed' 
                          : 'pending';
                        updateTaskStatus(task.id, nextStatus);
                      }}
                      className="mt-1"
                    >
                      {getStatusIcon(task.status)}
                    </button>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={`font-semibold text-lg ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {task.title}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          {getCategoryIcon(task.category)}
                          {task.category}
                        </span>
                      </div>

                      {task.description && (
                        <p className="text-gray-600 mb-3">{task.description}</p>
                      )}

                      {/* Progress Bar for tasks with target */}
                      {task.target_count && (
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Progresso</span>
                            <span className="font-medium">
                              {task.completed_count || 0} / {task.target_count}
                              <span className="text-gray-400 ml-1">
                                (±{task.tolerance_percent}%)
                              </span>
                            </span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min((task.completed_count / task.target_count) * 100, 100)}%` }}
                            />
                          </div>
                          <input
                            type="range"
                            min="0"
                            max={task.target_count}
                            value={task.completed_count || 0}
                            onChange={(e) => updateTaskProgress(task.id, parseInt(e.target.value))}
                            className="w-full mt-2"
                          />
                        </div>
                      )}

                      {/* Due Date */}
                      {task.due_date && (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          Prazo: {new Date(task.due_date).toLocaleDateString('pt-BR')}
                        </div>
                      )}

                      {/* Notes */}
                      {task.notes && task.notes.length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2">
                            <MessageSquare className="w-4 h-4" />
                            Notas
                          </div>
                          {task.notes.slice(-3).map((note, idx) => (
                            <p key={idx} className="text-sm text-gray-600">{note}</p>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* New Task Modal */}
      {showNewTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Nova Tarefa</h2>
            
            <form onSubmit={createTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Upload de imagens - Lote 1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Detalhes da tarefa..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <select
                    value={newTask.category}
                    onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="images">Imagens</option>
                    <option value="products">Produtos</option>
                    <option value="partner">Parceiros</option>
                    <option value="system">Sistema</option>
                    <option value="other">Outros</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta (quantidade)</label>
                  <input
                    type="number"
                    value={newTask.target_count}
                    onChange={(e) => setNewTask({ ...newTask, target_count: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prazo</label>
                  <input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewTask(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Criar Tarefa
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminTasksPage;
